import { type ChangeEvent, useState } from 'react';

import ActionsGroup from '../../../juristec-ui/core/ActionsGroup';
import Button from '../../../juristec-ui/core/Button';
import TextArea from '../../../juristec-ui/core/TextArea';
import InputTextLine from '../../../juristec-ui/core/InputTextLine';
import DatePicker from '../../../juristec-ui/core/DatePicker';

import SelectPlans, { type plansData } from './SelectPlans';

import { verifyInput, verifyLink } from '../../../juristec-ui/utils/validators/inputTextValidators';
import { trimString } from '../../../juristec-ui/utils/functions/formatString';
import { SelectOption } from '../../../types';

import {
  MainContainer,
  GridContainer,
  InstanceListContainer,
  InstanceList,
  TextAreaWrapper,
  OptionGroup,
  Option,
} from './styled/MultiMessages.styled';

const labelTitle: Record<string, string> = {
  info: 'AVISO',
  warning: 'ATENÇÃO',
  error: 'ERRO',
};

interface inputTextState {
  error: boolean,
  errorMsg: string,
  value: string,
}

interface MultiMessagesProps {
  hide: () => void;
  submit: (
    lvl: string,
    title: string,
    showUntil: Date | null,
    link: { [x: string]: string } | null,
    selectedInstances: (string | undefined)[],
    message: string
  ) => void;
  plans?: plansData;
}

const MultiMessages = ({
  hide, submit, plans,
}: MultiMessagesProps) => {
  const [msg, setMsg] = useState<inputTextState>({ error: true, errorMsg: '', value: '' });
  const [title, setTitle] = useState<inputTextState>({ error: true, errorMsg: '', value: '' });
  const [link, setLink] = useState<inputTextState>({ error: true, errorMsg: '', value: '' });
  const [linkMask, setLinkMask] = useState<inputTextState>({ error: true, errorMsg: '', value: '' });
  const [selected, setSelected] = useState<Record<string, SelectOption[]>>({});
  const [msgLvl, setMsgLvl] = useState<string>('info');

  const [showUntil, setShowUntil] = useState<string>('');

  const handleMsg = (val: string) => {
    const errMsg = verifyInput(val, true, 2, 500);
    setMsg({
      value: val,
      error: errMsg.length !== 0,
      errorMsg: errMsg,
    });
  };

  const handleMsgEvent = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    handleMsg(val);
  };

  const msgTrim = () => {
    handleMsg(trimString(msg.value));
  };

  const handleTitle = (val: string) => {
    const errMsg = verifyInput(val, false, 0, 50);
    setTitle({
      value: val,
      error: errMsg.length !== 0,
      errorMsg: errMsg,
    });
  };

  const handleTitleEvent = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    handleTitle(val);
  };

  const titleTrim = () => {
    handleTitle(trimString(title.value));
  };

  const handleLink = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value?.trim() || '';
    const errMsg = verifyLink(val);
    setLink({
      value: val,
      error: errMsg.length !== 0,
      errorMsg: errMsg,
    });
  };

  const handleLinkMask = (val: string) => {
    const errMsg = verifyInput(val, false, 0, 50);
    setLinkMask({
      value: val,
      error: errMsg.length !== 0,
      errorMsg: errMsg,
    });
  };

  const handleLinkMaskEvent = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    handleLinkMask(val);
  };

  const linkMaskTrim = () => {
    handleLinkMask(trimString(linkMask.value));
  };

  const handleSelection = (optsSelected: SelectOption[], plan: string) => {
    setSelected((o) => ({
      ...o, [plan]: optsSelected,
    }));
  };

  const blockSubmit = () => (
    msg.error
    || msg.value?.length <= 0
    || Object.keys(selected).length <= 0
    || Object.values(selected).some((e) => (e.length <= 0))
  );

  const showUntilDate = (dateSelected: string) => {
    if (showUntil) {
      const tmpDate = new Date(dateSelected);
      return new Date(tmpDate.setDate(tmpDate.getDate() + 1));
    } return null;
  };

  const handleSubmit = () => {
    submit(
      msgLvl,
      title.value?.length > 0 && !title.error ? title.value : labelTitle[msgLvl],
      showUntilDate(showUntil),
      link.value?.length > 0 && !link.error ? {
        [linkMask.value || 'Link externo']:
          link.value.startsWith('https://') ? link.value : `https://${link.value}`,
      } : null,
      Object.values(selected).flat().map((instance) => instance?.id),
      msg.value,
    );
  };

  return (
    <MainContainer>
      <GridContainer>
        <InstanceListContainer>
          <InstanceList>
            {Boolean(plans) && (
              <SelectPlans
                plans={plans!}
                states={selected}
                setter={handleSelection}
              />
            )}
          </InstanceList>
        </InstanceListContainer>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <OptionGroup>
            <Option
              value="info"
              onClick={() => setMsgLvl('info')}
              active={msgLvl === 'info'}
            >
              Aviso
            </Option>
            <Option
              value="warning"
              onClick={() => setMsgLvl('warning')}
              active={msgLvl === 'warning'}
            >
              Atenção
            </Option>
            <Option
              value="error"
              onClick={() => setMsgLvl('error')}
              active={msgLvl === 'error'}
            >
              Erro
            </Option>
          </OptionGroup>
          <div style={{ margin: '.2rem 0 .5rem' }}>
            {/* @ts-expect-error Wrong type */}
            <InputTextLine
              label="Título (opcional)"
              error={title.errorMsg.length > 0}
              errorMessage={title.errorMsg}
              value={title.value}
              onChange={handleTitleEvent}
              onBlur={titleTrim}
            />
          </div>
          <div style={{ margin: '.2rem 0 .5rem' }}>
            <DatePicker
              // @ts-expect-error Not typed yet
              value={showUntil}
              // @ts-expect-error Not typed yet
              onChange={setShowUntil}
              label="Mensagem disponível até (opcional)"
              style={{ width: '50%' }}
              readOnly={false}
              dateFormat="dd/MM/yyyy"
              atModal
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', margin: '.2rem 0 .5rem' }}>
            {/* @ts-expect-error Wrong type */}
            <InputTextLine
              label="Link externo (opcional)"
              error={link.errorMsg.length > 0}
              errorMessage={link.errorMsg}
              value={link.value}
              onChange={handleLink}
              wrapperStyle={{ marginRight: '.5rem' }}
            />
            {/* @ts-expect-error Wrong type */}
            <InputTextLine
              label="Máscara do link (opcional)"
              error={linkMask.errorMsg.length > 0}
              errorMessage={linkMask.errorMsg}
              value={linkMask.value}
              onChange={handleLinkMaskEvent}
              onBlur={linkMaskTrim}
            />
          </div>
          <TextAreaWrapper>
            <TextArea
              // @ts-expect-error Not typed yet
              label="Mensagem*"
              name="Mensagem"
              value={msg.value}
              error={msg.errorMsg.length > 0}
              errorMessage={msg.errorMsg}
              onChange={handleMsgEvent}
              onBlur={msgTrim}
            />
          </TextAreaWrapper>
        </div>
      </GridContainer>
      <ActionsGroup>
        <Button
          style={{ margin: '5px' }}
          onClick={hide}
          variant="outlined"
          size="large"
        >
          Cancelar
        </Button>
        <Button
          style={{ margin: '5px' }}
          size="large"
          disabled={blockSubmit()}
          onClick={handleSubmit}
        >
          Enviar
        </Button>
      </ActionsGroup>
    </MainContainer>
  );
};

export default MultiMessages;
